import Languages from '@shared/types/enums/languages';
import PlayerUtils from '@shared/utils/player';
import setListeners from './setListeners';
import { ITrack } from '@shared/types/interfaces/player';
import { checkState } from './mediaPlayer';
import { store } from '@shared/store/store';
import { PlayerState, PlayerType } from '@shared/types/enums/player';
import {
  playerEndAction,
  updateNextEpisode,
} from '@shared/store/thunks/player.thunk';
import {
  setBitrate,
  setCurrentTime,
  setDuration,
  setError,
  setLive,
  setPosition,
  setRange,
  setReady,
  setState,
  setTracks,
  setVod,
} from '@shared/store/slices/media.slice';

interface InitProps {
  drm?: { license?: string };
  headers?: {
    accessToken: string | null | undefined;
    contentType: string;
    deviceId: string;
    planId: string | undefined;
    platform: string;
    userId: string | undefined;
  };
  isSeeking: React.MutableRefObject<boolean>;
  position: {
    bookmark?: number | undefined;
    keepWatching?: number | undefined;
    start?: number | undefined;
  };
  source: string;
  setCue: React.Dispatch<React.SetStateAction<string>>;
}

/**
 * PlayerHelper object containing utility functions for AVPlayer initialization and management.
 * Handles player setup, error handling, data updates, and track management.
 */
const PlayerHelper = {
  /**
   * Initializes the AVPlayer with the provided source and configuration.
   * Sets up DRM, display dimensions, and prepares the player for playback.
   *
   * @param {InitProps} props - Initialization properties including source, headers, DRM, position, etc.
   */
  initPlayer: ({
    drm,
    headers,
    isSeeking,
    position,
    source,
    setCue,
  }: InitProps) => {
    setTimeout(() => {
      try {
        if (webapis?.avplay && headers) {
          // Stop previous content and load current source
          webapis.avplay.stop();
          webapis.avplay.close();
          if (checkState(['NONE', 'IDLE'])) {
            webapis.avplay.open(source);
          }
          // Set player listeners
          setListeners({
            headers,
            drm,
            updateData: () => PlayerHelper.updateData(isSeeking),
            setCue,
          });
          // Set drm properties
          if (checkState(['IDLE'])) {
            const properties = {
              AppSession: 'DrmSession_' + source,
              DataType: 'MPEG-DASH',
            };
            webapis.avplay.setDrm(
              'WIDEVINE_CDM',
              'SetProperties',
              JSON.stringify(properties)
            );
          }
          // Set canvas dimensions
          if (checkState(['IDLE', 'READY', 'PLAYING', 'PAUSED'])) {
            webapis.avplay.setDisplayRect(0, 0, 1920, 1080);
          }
          // Start content
          if (checkState(['IDLE', 'READY'])) {
            webapis.avplay.prepareAsync(
              () => PlayerHelper.onSuccess(isSeeking, position),
              (error: { message: string; code: number }) =>
                PlayerHelper.onError(error)
            );
          }
        }
      } catch (e) {
        console.error('Error while trying to init the player', e);
      }
    });
  },
  /**
   * Callback executed when player preparation succeeds.
   * Starts playback and seeks to bookmark or start position if specified.
   *
   * @param {React.MutableRefObject<boolean>} isSeeking - Ref to track if player is currently seeking
   * @param {Object} position - Position object containing bookmark or start time
   */
  onSuccess: (
    isSeeking: React.MutableRefObject<boolean>,
    position: {
      bookmark?: number | undefined;
      start?: number | undefined;
    }
  ) => {
    // Play content
    PlayerHelper.onCanPlay(isSeeking);

    // Seek to start position if required
    setTimeout(() => {
      if (checkState(['IDLE', 'READY', 'PLAYING', 'PAUSED'])) {
        if (position?.bookmark) {
          webapis.avplay.seekTo(position?.bookmark * 1000);
        } else if (position?.start) {
          webapis.avplay.seekTo(position?.start * 1000);
        }
      }
    });
  },
  /**
   * Callback executed when player preparation fails.
   * Parses error and dispatches error to Redux store.
   *
   * @param {Object} error - Error object containing message and code
   */
  onError: (error: { message: string; code: number }) => {
    console.error(error);
    const { media } = store.getState();
    const { error: errorSlice } = media;
    if (!errorSlice?.player) {
      const finalErrorMsg = PlayerUtils.getAvPlayerError(
        error?.message,
        error?.code
      );
      const errorToSend = {
        code: `${error?.code}`,
        player: PlayerType.AVPLAYER,
        message: finalErrorMsg,
      };
      store.dispatch(setError(errorToSend));
    }
  },
  /**
   * Callback executed when player is ready to play.
   * Starts playback, marks player as ready, updates data and tracks, and sets state to playing.
   *
   * @param {React.MutableRefObject<boolean>} isSeeking - Ref to track if player is currently seeking
   */
  onCanPlay: (isSeeking: React.MutableRefObject<boolean>) => {
    if (checkState(['READY', 'PLAYING', 'PAUSED'])) webapis.avplay.play();
    store.dispatch(setReady(true));
    PlayerHelper.updateData(isSeeking);
    PlayerHelper.updateTracks();
    store.dispatch(setState(PlayerState.PLAYING));
  },
  /**
   * Updates playback data including duration, position, seek range, and bitrate.
   * Calculates position differently for live vs VOD content.
   * Updates analytics with current rendition information.
   *
   * @param {React.MutableRefObject<boolean>} isSeeking - Ref to track if player is currently seeking
   */
  updateData: (isSeeking: React.MutableRefObject<boolean>) => {
    try {
      if (!checkState(['READY', 'PLAYING', 'PAUSED'])) return;
      if (isSeeking.current) return;
      // Check if content is live
      const live = webapis.avplay.getStreamingProperty('IS_LIVE') === '1';
      const StartTime = parseInt(
        webapis.avplay.getStreamingProperty('GET_LIVE_DURATION').split('|')[0]
      );
      const EndTime = parseInt(
        webapis.avplay.getStreamingProperty('GET_LIVE_DURATION').split('|')[1]
      );

      // Calculate duration: DVR window size for live, total duration for VOD
      const duration = live
        ? EndTime - StartTime
        : webapis.avplay.getDuration() / 1000 || 0;

      // Calculate position: timestamp for live, current time for VOD
      const positionSet = live
        ? Date.now() - (EndTime - webapis.avplay.getCurrentTime())
        : webapis.avplay.getCurrentTime() / 1000;

      // Calculate seek range: sliding window for live, fixed range for VOD
      const seek = live
        ? {
            start: Date.now() - duration,
            end: Date.now(),
          }
        : { start: 0, end: duration || 0 };

      // If the end is surpassed, finish playback
      if (positionSet >= (duration || 0) && !live) {
        store.dispatch(playerEndAction());
      }

      // Update media slice with current playback information
      store.dispatch(
        setCurrentTime(
          live
            ? webapis.avplay.getCurrentTime()
            : webapis.avplay.getCurrentTime() / 1000 || 0
        )
      );
      store.dispatch(setDuration(duration));
      store.dispatch(setRange({ seek }));
      if (live) {
        store.dispatch(setLive());
      } else {
        store.dispatch(setVod());
      }
      store.dispatch(setPosition({ position: positionSet, seek: false })); // Set position last is best
      store.dispatch(updateNextEpisode());

      // Update bitrate and analytics with current rendition information
      try {
        const videoInfo = JSON.parse(
          webapis.avplay
            .getCurrentStreamInfo()
            .find((item) => item.type === 'VIDEO').extra_info
        );
        const bitrate = videoInfo.Bit_rate;
        store.dispatch(setBitrate(bitrate));
      } catch (error) {
        console.error(error);
      }
    } catch (e) {
      console.error('Error while trying to update player data', e);
    }
  },
  /**
   * Updates available audio and subtitle tracks from the player.
   * Parses track information and maps language codes to application language enum.
   * Dispatches tracks to Redux store.
   */
  updateTracks: () => {
    try {
      if (!checkState(['READY', 'PLAYING', 'PAUSE'])) return;
      const audioTracks: ITrack[] = [];
      const textTracks: ITrack[] = [];
      /**
       * Parses language codes from player format to application language enum.
       * Handles both 3-letter codes (eng, spa, cat) and 2-letter codes (en, es, ca).
       *
       * @param {string} lan - Language code from player
       * @returns {Languages} Application language enum value
       */
      const parseTrack = (lan: string) => {
        switch (lan) {
          case 'eng':
            return Languages.ENGLISH;
          case 'spa':
            return Languages.SPANISH;
          case 'cat':
            return Languages.CATALAN;
          case 'en':
          case 'es':
          case 'ca':
            return lan;
        }
        return Languages.ORIGINAL;
      };
      // Get all available tracks from player
      const tracks = webapis.avplay.getTotalTrackInfo();
      tracks.forEach(
        (track: {
          type: string;
          index: number;
          extra_info: string | { language: string; track_lang: string };
        }) => {
          // Process audio tracks
          if (track.type === 'AUDIO') {
            track.extra_info =
              typeof track.extra_info === 'string'
                ? JSON.parse(track.extra_info)
                : track.extra_info;
            if ((track.extra_info as { language: string }).language) {
              audioTracks.push({
                id: `${track.index}`,
                language: parseTrack(
                  (track.extra_info as { language: string }).language
                ) as Languages,
              });
            }
          } else if (track.type === 'TEXT') {
            // Process subtitle/text tracks
            track.extra_info =
              typeof track.extra_info === 'string'
                ? JSON.parse(track.extra_info)
                : track.extra_info;
            textTracks.push({
              id: `${track.index}`,
              language: parseTrack(
                (track.extra_info as { track_lang: string }).track_lang
              ) as Languages,
            });
          }
        }
      );
      store.dispatch(setTracks({ audio: audioTracks, subtitles: textTracks }));
    } catch (e) {
      console.error('Audio tracks or text tracks not available', e);
    }
  },
};

export default PlayerHelper;
